import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { SelectProps } from '@amzn/awsui-components-react/polaris/select';
import { featureIsEnabled, useFeatures } from '../features';
import { bundlesForKey } from '../localizedText';
import { Messages, useBundles } from '../messages';
import { toArbValueId } from '../../utils/arbIds';
import { LOCALIZATION } from '../../utils/constants';

/** Where an option list's labels come from, in each of the two systems. */
export type LocalizedOptionList = {
  /** Key of the legacy i18next list, read wholesale when the Localization flag is off. */
  i18nKey: string;
  /** ARB id prefix the labels are keyed under: `<idPrefix>_<value>`, e.g. `focus_zone`. */
  idPrefix: string;
  /** The value codes, in display order. Code-owned — never derived from the bundle. */
  values: readonly string[];
  /** Also read `<idPrefix>_<value>_description`, for a list whose options carry descriptions. */
  withDescriptions?: boolean;
};

/** Flag off: the legacy i18next list, whatever shape it holds (`focus` and `activity` are grouped). */
function useLegacyOptions(namespaces: string[], i18nKey: string): SelectProps.Options {
  const { t } = useTranslation(namespaces);
  return t(i18nKey, { returnObjects: true }) as SelectProps.Options;
}

/**
 * Flag on: rebuild the list from the ARB bundle, which is a flat id→string map and so cannot return
 * an array. Null when the bundle cannot serve every label, so the caller can show the legacy list.
 *
 * `useArb` is passed in rather than read here so that, with the flag off, this reads nothing at all —
 * a failed language pack must not report errors on a path where ARB is inert.
 */
function useArbOptions(
  namespaces: string[],
  list: LocalizedOptionList,
  useArb: boolean
): SelectProps.Options | null {
  const bundles = (useArb && bundlesForKey(list.i18nKey, namespaces)) || [];
  const readers = useBundles(bundles);
  // `bundles` is a fresh array every render, so the memo below keys off this string instead and
  // rebuilds the names from it. Depending on the array itself would bust the memo on every render.
  const bundlesKey = bundles.join('\u0000');

  // Memoised because a list is rebuilt id by id: an event card re-rendering for an unrelated reason
  // would otherwise re-read every label of every list it shows.
  return useMemo(() => {
    // A namespace can span several bundles, so try each in the order t() would, and take the first
    // that holds the id. No id is in two of them, so the order never decides which string is served.
    const candidates = (bundlesKey ? bundlesKey.split('\u0000') : [])
      .map((bundle) => readers[bundle])
      .filter((reader): reader is Messages => reader !== undefined);
    if (candidates.length === 0) return null;

    const read = (id: string): string => {
      for (const reader of candidates) {
        const text = reader.format(id);
        if (text) return text;
      }
      return '';
    };

    const options: SelectProps.Option[] = [];
    for (const value of list.values) {
      const id = `${list.idPrefix}_${toArbValueId(value)}`;
      const label = read(id);
      const description = list.withDescriptions ? read(`${id}_description`) : '';
      // Blank means the pack is still loading or failed to load, or the id is missing from the source
      // bundles altogether. Abandon the whole list rather than filling the gap, so nobody is shown a
      // half-translated one. A locale that merely lacks a translation is not this case: arb-tools
      // serves English from the default pack, and the list is built as normal.
      if (!label || (list.withDescriptions && !description)) return null;
      options.push(list.withDescriptions ? { value, label, description } : { value, label });
    }
    return options;
  }, [bundlesKey, readers, list]);
}

/**
 * An option list's labels, from RedVelvetTranslations or the legacy i18next list, chosen by the
 * Localization flag. The replacement for `t(key, { returnObjects: true })`, which ARB bundles cannot
 * answer.
 *
 * `namespaces` is the array passed to useLocalizedText alongside it, and resolves `i18nKey` the same
 * way: a `ns:` prefix names the namespace, otherwise the first entry.
 *
 * The two systems return different shapes — the ARB list is always flat, the legacy list may carry
 * option groups — so a caller must handle both. Looking a label up by value (getOptionFromValue)
 * does; rendering the list straight into a Select would show group headers only while the flag is off.
 */
export function useTOptions(namespaces: string[], list: LocalizedOptionList): SelectProps.Options {
  const useArb = featureIsEnabled(LOCALIZATION, useFeatures(), useSearchParams()[0]);
  const legacy = useLegacyOptions(namespaces, list.i18nKey);
  const localized = useArbOptions(namespaces, list, useArb);

  return localized ?? legacy;
}
